import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import { queryAPI } from '../services/api';
import './QueryHistory.css';

/** Page size aligned with backend /api/history (max 1000). */
const HISTORY_PAGE_SIZE = 500;

const QueryHistory = ({ onLoadQuery, visible }) => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [filter, setFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const totalFetchedRef = useRef(0);

  useEffect(() => {
    if (visible !== false) {
      loadHistory();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  const loadHistory = async () => {
    setLoading(true);
    setLoadingMore(false);
    try {
      const response = await queryAPI.getHistory({ limit: HISTORY_PAGE_SIZE, skip: 0 });
      const data = response?.data;
      const list = Array.isArray(data?.history) ? data.history : [];
      totalFetchedRef.current = list.length;
      setHistory(list);
      setHasMore(list.length >= HISTORY_PAGE_SIZE);
    } catch (error) {
      const message = error.response?.data?.error || error.message || 'Failed to load query history';
      toast.error(message);
      totalFetchedRef.current = 0;
      setHistory([]);
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  };

  const loadOlder = async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    try {
      const skip = totalFetchedRef.current;
      const response = await queryAPI.getHistory({ limit: HISTORY_PAGE_SIZE, skip });
      const data = response?.data;
      const list = Array.isArray(data?.history) ? data.history : [];
      totalFetchedRef.current += list.length;
      setHistory((prev) => [...prev, ...list]);
      setHasMore(list.length >= HISTORY_PAGE_SIZE);
    } catch (error) {
      const message = error.response?.data?.error || error.message || 'Failed to load older history';
      toast.error(message);
    } finally {
      setLoadingMore(false);
    }
  };

  const filteredHistory = history.filter(item => {
    const q = (item && item.query) ? String(item.query) : '';
    const matchesSearch = filter === '' ||
      q.toLowerCase().includes((filter || '').toLowerCase());
    const matchesStatus = statusFilter === 'all' ||
      (item && item.status) === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const handleLoadQuery = (query) => {
    if (onLoadQuery) {
      onLoadQuery(query);
      toast.success('Query loaded into editor');
    }
  };

  const formatDate = (dateString) => {
    if (dateString == null || dateString === '') return '—';
    try {
      const date = new Date(dateString);
      return Number.isNaN(date.getTime()) ? String(dateString) : date.toLocaleString();
    } catch {
      return String(dateString);
    }
  };

  if (loading) {
    return (
      <div className="query-history">
        <div className="history-loading">
          <div className="spinner"></div>
          <p>Loading history...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="query-history">
      <div className="history-header">
        <h2>Query History</h2>
        <button type="button" onClick={loadHistory} className="btn-refresh" title="Refresh">
          Refresh
        </button>
      </div>

      <div className="history-filters">
        <input
          type="text"
          className="filter-input"
          placeholder="Search queries..."
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        />
        <select 
          className="filter-select"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="all">All Status</option>
          <option value="success">Success</option>
          <option value="error">Error</option>
        </select>
      </div>

      <div className="history-stats">
        <div className="stat">
          <span className="stat-label">{hasMore ? 'Loaded (newest batch first):' : 'Queries loaded:'}</span>
          <span className="stat-value">{history.length}</span>
        </div>
        <div className="stat">
          <span className="stat-label">Success:</span>
          <span className="stat-value success">
            {history.filter(h => h.status === 'success').length}
          </span>
        </div>
        <div className="stat">
          <span className="stat-label">Errors:</span>
          <span className="stat-value error">
            {history.filter(h => h.status === 'error').length}
          </span>
        </div>
      </div>

      <div className="history-list">
        {filteredHistory.length === 0 ? (
          <div className="history-empty">
            <p>{filter ? 'No queries match your search' : 'No query history yet'}</p>
          </div>
        ) : (
          filteredHistory.map((item, index) => (
            <div 
              key={item.id || `hist-${index}`}
              className={`history-item ${item.status || 'success'}`}
              onClick={() => handleLoadQuery(item.query != null ? item.query : '')}
              title="Click to load this query"
            >
              <div className="history-item-header">
                <span
                  className={`status-badge ${item.status || 'success'}`}
                  title={(item.status || 'success') === 'success' ? 'Success' : 'Error'}
                >
                  {(item.status || 'success') === 'success' ? 'OK' : 'Err'}
                </span>
                <span className="history-date">{formatDate(item.created_at)}</span>
                <span className="history-time">
                  {item.execution_time != null && typeof item.execution_time === 'number'
                    ? `${item.execution_time.toFixed(2)}s`
                    : ''}
                </span>
              </div>
              <div className="history-query">
                <code>{item.query != null ? String(item.query) : ''}</code>
              </div>
              {item.error_message && (
                <div className="history-error">
                  <strong>Error:</strong> {item.error_message}
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {hasMore && (
        <div className="history-footer">
          <button
            type="button"
            className="btn-load-more"
            onClick={loadOlder}
            disabled={loadingMore}
          >
            {loadingMore ? 'Loading…' : 'Load older queries'}
          </button>
        </div>
      )}
    </div>
  );
};

export default QueryHistory;

